// ExplodedStack - the floors stacked in an oblique view. Every drawn system
// is layered onto its floor as one path per level|system|zone bucket
// (guide 9.7), so toggling a system or recolouring for progress touches a
// handful of nodes, not thousands. The rooms of a room-highlight system
// (telecom rooms) are filled and their risers drawn between levels; the
// roof plate carries the rooftop units. Zones are hover targets: the
// parent shows the zone large and opens the zone page on click.
import React, { useMemo } from "react";
import {
  CATEGORY_FILL, ROOF, STATUS_COLOR, bounds, bucketKey, centroid, dotsPath, drawnSystems, linesPath, makeStackProjector,
  marksPath, pointsAttr, polysPath, profileAt, roomLabel, sortedLevels, systemColor,
} from "./glanceModel";

function UnitBox({ project, rank, x, y, on, id, ghost }) {
  const w = 34; const d = 16; const h = 26;
  const a = project(x, y, rank); const b = project(x + w, y, rank); const c = project(x + w, y + d, rank); const e = project(x, y + d, rank);
  const up = (q) => [q[0], q[1] - h];
  const pts = (arr) => arr.map((q) => q.map((v) => v.toFixed(1)).join(",")).join(" ");
  const top = up(project(x + w / 2, y + d / 2, rank));
  const fill = (f) => (ghost ? "none" : f);
  const dash = ghost ? "4 3" : undefined;
  return (
    <g className={on && !ghost ? "g-unit-on" : "g-unit-off"}>
      <polygon points={pts([a, b, up(b), up(a)])} fill={fill("var(--rtuside)")} stroke="var(--ink)" strokeWidth="1" strokeDasharray={dash} />
      <polygon points={pts([b, c, up(c), up(b)])} fill={fill("var(--rtuside)")} stroke="var(--ink)" strokeWidth="1" strokeDasharray={dash} />
      <polygon points={pts([up(a), up(b), up(c), up(e)])} fill={fill(on ? "var(--occ)" : "var(--rtutop)")} fillOpacity={on ? 0.55 : 1} stroke="var(--ink)" strokeWidth="1" strokeDasharray={dash} />
      {!ghost && [-8, 0, 8].map((dx) => (
        <line key={dx} className="g-air" x1={top[0] + dx} y1={top[1] - 4} x2={top[0] + dx} y2={top[1] - 14} stroke="var(--occ)" strokeWidth="2" strokeLinecap="round" />
      ))}
      <text x={top[0]} y={top[1] - 22} textAnchor="middle" fontWeight="600" fontSize={ghost ? 13 : 15} fill={ghost ? "var(--ink2)" : "var(--ink)"} paintOrder="stroke" stroke="var(--sheet)" strokeWidth="4">
        {ghost ? `${id} (location not in received drawings)` : id}
      </text>
    </g>
  );
}

// Small per-system lift so layers on one floor separate a little: below
// the slab sits under the plate, ducts and pipes ride highest.
const liftFor = (s) => (s.tier == null ? 0 : s.tier < 0 ? -5 : [1, 4, 7, 10, 12, 0][Math.min(5, s.tier)]);

function BucketLayer({ bucket, system, project, rank, color, opacity }) {
  const lift = liftFor(system);
  const proj = (x, y) => { const p = project(x, y, rank); return [p[0], p[1] - lift]; };
  const sw = system.id === "ducts" ? 0.55 : system.draw === "lines" ? 0.8 : 0.7;
  return (
    <g opacity={opacity} style={{ pointerEvents: "none" }}>
      {bucket.lines && <path d={linesPath(bucket.lines, proj)} stroke={color} strokeWidth={sw} fill="none" strokeDasharray={system.dashed ? "3 2" : undefined} />}
      {bucket.polys && <path d={polysPath(bucket.polys, proj)} fill={color} stroke="var(--systemink)" strokeWidth="0.3" />}
      {bucket.dots && <path d={dotsPath(bucket.dots, proj, system.id === "power" ? 1.1 : 0.9)} fill={color} />}
      {bucket.marks && <path d={marksPath(bucket.marks, (x, y) => { const p = proj(x, y); return [p[0], p[1] - 2]; }, 3.2)} fill={color} stroke="var(--sheet)" strokeWidth="0.6" />}
    </g>
  );
}

// The host model's tracked elements on a plate (BA03 step 3): each occupied
// cell of the coarse grid drawn as a 3 px square at its projected centre, all
// in one path - a 5 ft cell on a 1,700 ft plate is a sliver otherwise.
function ModelCells({ cells, project, rank, color }) {
  if (!cells.length) return null;
  const d = cells.map(([x, y]) => { const p = project(x, y, rank); return `M${(p[0] - 1.5).toFixed(1)} ${(p[1] - 1.5).toFixed(1)}h3v3h-3Z`; }).join("");
  return <path d={d} fill={color} opacity="0.85" style={{ pointerEvents: "none" }} />;
}

export default function ExplodedStack({
  manifest, system, dayType, hour, selectedRoomId, onSelectRoom,
  active, mode = "systems", statuses, activeZone, onZoneEnter, onZoneLeave, onZoneOpen, modelLayer = null,
}) {
  const levels = useMemo(() => sortedLevels(manifest), [manifest]);
  const roof = manifest.roof;
  const drawn = drawnSystems(manifest);
  const buckets = manifest.buckets || {};
  const zones = manifest.zones || [];
  const roofSystem = drawn.find((s) => s.id === ROOF);
  const roofOutline = (roof && levels.find((l) => l.id === roof.outline)) || (roofSystem ? levels[levels.length - 1] : null);
  const plates = levels.length + (roofOutline ? 1 : 0);
  const b = useMemo(() => bounds(levels), [levels]);
  const { project, height, width } = useMemo(() => makeStackProjector(b, plates), [b, plates]);

  const isOn = (id) => !active || active.has(id);
  // With drawn systems the room highlight follows its chip; without them
  // (a rooms-only manifest) it is always on, as before.
  const highlight = system && (!drawn.length || isOn(system.id)) ? system : null;
  const systemRooms = new Set((highlight && highlight.rooms) || []);
  const hasSystems = (manifest.systems || []).length > 0;
  const operations = manifest.operations;
  const dayMode = mode === "day" || (!drawn.length && !!operations);
  const occ = dayMode && operations ? profileAt(operations, "occupancy", dayType, hour) : null;
  const useOpacity = occ == null ? (drawn.length ? 0.55 : 1) : 0.18 + (0.82 * occ) / 100;

  const where = {};
  levels.forEach((l, i) => l.rooms.forEach((r) => { where[r.id] = { room: r, rank: i + 1 }; }));
  const midX = b.xmin + b.w / 2;

  const equipment = (roof && roof.equipment) || [];
  const roofRank = levels.length + 1;
  // A unit the documents do not locate may still be tagged on a roof plan
  // that was received: take its position from the roof bucket's marks.
  const roofMarks = Object.entries(buckets).filter(([k]) => k.startsWith(`${ROOF}|`)).flatMap(([, bk]) => bk.marks || []);
  const placed = equipment.map((e) => {
    if (e.position) return { e, at: e.position, located: true };
    const m = roofMarks.find((q) => String(q[2] || "").toUpperCase().startsWith(String(e.id).toUpperCase()));
    return m ? { e, at: [m[0] - 17, m[1] - 8], located: true } : { e, at: null, located: false };
  });
  // Guide 23.3: equipment paints back to front (farther along the plan's y first), located units before the schematic ones.
  const units = placed.filter((p) => p.e.kind !== "antenna").slice().sort((a, c) => (a.at && c.at ? c.at[1] - a.at[1] : a.at ? -1 : c.at ? 1 : 0));
  const masts = placed.filter((p) => p.e.kind === "antenna");
  const schematic = placed.some((p) => !p.located);

  const layerColor = (s, key) => {
    if (mode !== "progress") return { color: systemColor(s), opacity: dayMode ? 0.45 : 1 };
    const st = statuses && statuses[key];
    if (!st) return { color: systemColor(s), opacity: 0.22 };
    return { color: STATUS_COLOR[st.st], opacity: st.st === "todo" ? 0.35 : 1 };
  };

  const zoneTargets = (levelId, rank) => zones.map((z) => {
    const q = [[Math.max(z.x0, b.xmin - 6), b.ymin - 6], [Math.min(z.x1, b.xmax + 6), b.ymin - 6], [Math.min(z.x1, b.xmax + 6), b.ymax + 6], [Math.max(z.x0, b.xmin - 6), b.ymax + 6]];
    const id = `${levelId}|${z.index}`;
    return (
      <polygon key={`z-${id}`} className={`g-zone${activeZone === id ? " is-active" : ""}`}
        points={pointsAttr(q, (p) => project(p[0], p[1], rank))} tabIndex={0} role="button"
        aria-label={`${levelId === ROOF ? "Roof" : (levels[rank - 1] || {}).name || levelId}, ${z.label}`}
        onMouseEnter={(e) => onZoneEnter && onZoneEnter(levelId, z.index, e)}
        onMouseMove={(e) => onZoneEnter && onZoneEnter(levelId, z.index, e, true)}
        onMouseLeave={() => onZoneLeave && onZoneLeave()}
        onFocus={() => onZoneEnter && onZoneEnter(levelId, z.index, null)}
        onBlur={() => onZoneLeave && onZoneLeave()}
        onClick={(e) => onZoneOpen && onZoneOpen(levelId, z.index, e)}
        onKeyDown={(e) => { if (e.key === "Enter" && onZoneOpen) onZoneOpen(levelId, z.index, e, true); }} />
    );
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img"
         aria-label={`Exploded stack of ${levels.length} floor plates${roofOutline ? " and the roof" : ""}${highlight ? `, ${highlight.label} highlighted` : ""}`}>
      {levels.map((l, i) => {
        const rank = i + 1;
        const lp = project(b.xmin, b.ymin, rank);
        return (
          <g key={l.id}>
            {l.footprint && <polygon points={pointsAttr(l.footprint, (p) => project(p[0], p[1], rank))} fill="var(--sheet)" stroke="var(--ink)" strokeWidth="1.4" />}
            {l.rooms.map((r) => {
              const inSystem = systemRooms.has(r.id);
              const isUse = !inSystem && r.category && r.category !== "circ" && r.category !== "tel";
              const fill = inSystem ? "var(--system)"
                : r.category === "tel" && hasSystems ? "var(--sup)"
                  : CATEGORY_FILL[r.category] || "var(--circ)";
              return (
                <polygon key={r.id}
                  className={`g-room${isUse ? " g-use" : ""}${selectedRoomId === r.id ? " is-selected" : ""}`}
                  points={pointsAttr(r.polygon, (p) => project(p[0], p[1], rank))}
                  fill={fill} fillOpacity={isUse ? useOpacity : inSystem ? 1 : (drawn.length ? 0.55 : 1)}
                  stroke={inSystem ? "var(--systemink)" : "var(--line)"} strokeWidth={inSystem ? 1.2 : 0.4}
                  onClick={() => onSelectRoom(r.id)}>
                  <title>{roomLabel(r)}</title>
                </polygon>
              );
            })}
            {/* Guide 23.3: systems paint from the lowest band up (below slab first). */}
            {drawn.filter((s) => s.id !== ROOF && isOn(s.id)).slice().sort((a, c) => (a.tier == null ? 0 : a.tier) - (c.tier == null ? 0 : c.tier)).map((s) => zones.map((z) => {
              const key = bucketKey(l.id, s.id, z.index);
              const bucket = buckets[key];
              if (!bucket) return null;
              const { color, opacity } = layerColor(s, key);
              return <BucketLayer key={key} bucket={bucket} system={s} project={project} rank={rank} color={color} opacity={opacity} />;
            }))}
            {modelLayer && <ModelCells cells={(modelLayer.byLevel[l.id] || [])} project={project} rank={rank} color={modelLayer.color} />}
            <text x={lp[0] - 4} y={lp[1] + 22} fontSize="17" fontWeight="600" fill="var(--ink)">{l.name || l.id}</text>
          </g>
        );
      })}

      {highlight && (highlight.stacks || []).map((chain, k) => chain.slice(1).map((id, i) => {
        const p = where[chain[i]]; const q = where[id];
        if (!p || !q) return null;
        const A = project(...centroid(p.room.polygon), p.rank);
        const B = project(...centroid(q.room.polygon), q.rank);
        return <line key={`${k}-${i}`} x1={A[0]} y1={A[1]} x2={B[0]} y2={B[1]} stroke="var(--riser)" strokeWidth="3" strokeDasharray="7 5" style={{ pointerEvents: "none" }} />;
      }))}
      {highlight && (highlight.rooms || []).map((id) => {
        const w = where[id];
        if (!w) return null;
        const c = centroid(w.room.polygon);
        const p = project(c[0], c[1], w.rank);
        const west = c[0] < midX;
        return (
          <text key={id} x={p[0] + (west ? -14 : 14)} y={p[1] - 10} textAnchor={west ? "end" : "start"}
                fontSize="15" fontWeight="600" fill="var(--ink)" paintOrder="stroke" stroke="var(--sheet)" strokeWidth="4"
                style={{ pointerEvents: "none" }}>
            {roomLabel(w.room, highlight)}
          </text>
        );
      })}

      {roofOutline && (!roofSystem || isOn(ROOF)) && (
        <g>
          <polygon points={pointsAttr(roofOutline.footprint || [], (p) => project(p[0], p[1], roofRank))} fill="var(--roof)" stroke="var(--ink)" strokeWidth="1.2" strokeDasharray="6 4" />
          {(() => { const lp = project(b.xmin, b.ymin, roofRank); return (
            <>
              <text x={lp[0] - 4} y={lp[1] + 22} fontSize="17" fontWeight="600" fill="var(--ink)">Roof</text>
              <text x={lp[0] + 50} y={lp[1] + 22} fontSize="14" fill="var(--ink2)">
                {[roof && roof.assembly && roof.assembly.text, roofSystem && roofSystem.coverage && roofSystem.coverage.note,
                  schematic ? "Units the documents don't locate are drawn dashed." : ""].filter(Boolean).join(". ")}
              </text>
            </>
          ); })()}
          {roofSystem && zones.map((z) => {
            const key = bucketKey(ROOF, ROOF, z.index);
            const bucket = buckets[key];
            if (!bucket) return null;
            // Tagged units are drawn as boxes below; the rest of the roof
            // bucket (condensers, fans, piping) as marks and dots.
            const rest = { ...bucket, marks: (bucket.marks || []).filter((m) => !units.some((u) => String(m[2] || "").toUpperCase().startsWith(String(u.e.id).toUpperCase()))) };
            const { color, opacity } = layerColor(roofSystem, key);
            return <BucketLayer key={key} bucket={rest} system={{ ...roofSystem, tier: null }} project={project} rank={roofRank} color={mode === "progress" ? color : "var(--ink2)"} opacity={opacity} />;
          })}
          {units.map((u, i) => {
            const x = u.at ? u.at[0] : b.xmin + ((i + 0.5) / units.length) * b.w - 17;
            const y = u.at ? u.at[1] : b.ymin + b.h / 2 - 8;
            const on = dayMode ? !!profileAt(operations, u.e.schedule || "always", dayType, hour) : true;
            return <UnitBox key={u.e.id} project={project} rank={roofRank} x={x} y={y} on={on && dayMode} id={u.e.id} ghost={!u.located && drawn.length > 0} />;
          })}
          {masts.map((m, i) => {
            const p = project(m.at ? m.at[0] : b.xmax - 30 - i * 40, m.at ? m.at[1] : b.ymax - 20, roofRank);
            return (
              <g key={m.e.id} style={{ pointerEvents: "none" }}>
                <line x1={p[0]} y1={p[1]} x2={p[0]} y2={p[1] - 48} stroke="var(--ink)" strokeWidth="2.5" />
                <rect x={p[0] - 5} y={p[1] - 54} width="10" height="10" fill="var(--system)" stroke="var(--systemink)" />
                <text x={p[0] - 12} y={p[1] - 44} textAnchor="end" fontSize="14" fill="var(--ink)" paintOrder="stroke" stroke="var(--sheet)" strokeWidth="4">{m.e.label || m.e.id}</text>
              </g>
            );
          })}
        </g>
      )}

      {zones.length > 0 && onZoneEnter && levels.map((l, i) => <g key={`zt-${l.id}`}>{zoneTargets(l.id, i + 1)}</g>)}
      {zones.length > 0 && onZoneEnter && roofOutline && roofSystem && isOn(ROOF) && zoneTargets(ROOF, roofRank)}
    </svg>
  );
}
